import React, {CSSProperties, useEffect, useState} from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import {
    DialogActions,
    DialogContent,
    DialogContentText,
    IconButton,
    InputAdornment,
    SxProps,
    TextField
} from '@mui/material';
import Box from '../Common/Box';
import Button from '@mui/material/Button';
import shadowClient from '../lib/clients/shadow/shadowClient';
import {observer} from 'mobx-react';
import utils from '../lib/utils';
import {FileInput} from '../Common/FileInput';
import AddPhotoAlternateRounded from '@mui/icons-material/AddPhotoAlternateRounded';
import {Alert} from '@mui/lab';
import ControlButton from '../Common/ControlButton';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import Logger from '../lib/Logger';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import {ColorButton} from '../Common/ColorButton';
import PortalledSnackbar from '../Common/PortalledSnackbar';
import {User} from "../lib/stores/UserStore";


const styles: { avatar: CSSProperties, avatarContainer: SxProps, fileInput: SxProps } = {
    avatar: {
        minWidth: "30px",
        minHeight: "30px",
        width: "128px",
        height: "128px",
        borderRadius: "10000px",
        marginRight: "10px",
        position: "relative",
        // @ts-ignore
        "&:after": {
            content: '"\\f1c5" " " attr(alt)',
            fontSize: "0",
            fontFamily: "FontAwesome",
            color: "rgb(100, 100, 100)",
            display: "block",
            position: "absolute",
            zIndex: 2,
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
        },
        WebkitBoxShadow:
                "0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2)",
        boxShadow:
                "0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2)",
        objectFit: "cover",
        objectPosition: "center center"
    },
    avatarContainer: {
        marginTop: "6px",
        position: "relative"
    },
    fileInput: {
        cursor: "pointer",
        position: "absolute",
        top: "0",
        right: "0",
        fontSize: "16px"
    },
};

const logger = new Logger("ShadowChat");

interface SettingsProps {
    clientUser: User;
    sx?: SxProps;
}

const UserSettings: React.FC<SettingsProps> = ({clientUser, sx}) => {

    // const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [localAvatarUrl, setLocalAvatarUrl] = useState("");
    const [loginToken, setLoginToken] = useState("");
    const [b64Avatar, setB64Avatar] = useState<string>("");
    const [username, setUsername] = useState("");
    const [notificationMessage, setNotificationMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const clientRoomId = shadowClient.getJoinedRoomId();
    const [joinedRoomId, setJoinedRoomId] = useState(clientRoomId);

    useEffect(() => {
        if (clientUser.login_token) {
            setLoginToken(clientUser.login_token);
        }
    }, [clientUser.login_token])

    useEffect(() => {
        setUsername(clientUser.username);
    }, [clientUser])

    useEffect(() => {
        if (clientRoomId && joinedRoomId !== clientRoomId) {
            setJoinedRoomId(clientRoomId);
        }
    }, [clientRoomId])


    useEffect(() => {
        if (clientUser['avatar']) {
            setB64Avatar("")
        }
    }, [clientUser['avatar']])


    const avatarUrl = utils.createAvatarUrl(clientUser['avatar']);
    const avatarSrc = avatarUrl || localAvatarUrl;

    // @ts-ignore
    return (
            <Box overflow={"auto"} height={"100%"} width={"100%"} sx={sx}>
                <Box alignItems={"center"} row width={"100%"}>

                    <DialogTitle sx={{marginRight: "auto"}}>
                        Account
                    </DialogTitle>

                </Box>
                <DialogContent>

                    <DialogContentText>
                        Change your profile or set a different login token. Copy your current token to regain access to
                        this account
                        upon future visits.
                    </DialogContentText>

                    <Box
                            row
                            width={"100%"}
                            center
                            padding={"20px 0px"}
                    >
                        <Box
                                sx={styles.avatarContainer}
                        >
                            <img
                                    crossOrigin={"anonymous"}
                                    style={styles.avatar}
                                    src={b64Avatar ? b64Avatar : avatarSrc}
                                    alt={'avatar'}
                                    // style={{
                                    //   backgroundImage: `url(${b64Avatar ? b64Avatar : avatarSrc})`,
                                    // }}
                                    onError={() => {
                                        if (!localAvatarUrl) {
                                            setLocalAvatarUrl(utils.createAvatarUrl())
                                        }

                                    }}
                            />
                            <FileInput
                                    onClick={files => {
                                        const reader = new FileReader();

                                        //@todo this might cause issues (setB64Avatar reference changes)
                                        reader.addEventListener("load", () => {
                                            if (typeof reader.result !== "string") {
                                                throw new Error("result isn't a string");
                                            }

                                            setB64Avatar(reader.result);

                                        }, false);

                                        if (files && files[0]) {
                                            reader.readAsDataURL(files[0]);
                                        }
                                    }}
                                    sx={styles.fileInput}
                                    // visible={editEnabled}
                                    visible={true}
                                    customComponent={() => {
                                        return <Box center>
                                            <AddPhotoAlternateRounded sx={{
                                                fontSize: "40px",
                                            }}/>
                                        </Box>
                                    }}
                            />
                        </Box>

                    </Box>

                    {saveError && <Alert
                            sx={{margin: "15px 0"}}
                            variant={"outlined"}
                            severity={"error"}
                            onClose={() => setSaveError("")}
                    >
                        {saveError}
                    </Alert>
                    }

                    <TextField
                            multiline
                            maxRows={1}
                            margin="dense"
                            id="user_name"
                            label="Username"
                            type="text"
                            fullWidth
                            variant="standard"
                            value={clientUser.username || ""}
                            onChange={(event) => {
                                clientUser.username = event.target.value;
                            }}
                    />


                </DialogContent>
                <DialogActions>

                    <Button
                            disabled={clientUser.username === username && !b64Avatar}
                            onClick={() => {
                                clientUser.username = username;
                                setB64Avatar("");
                            }}>
                        Cancel
                    </Button>

                    <ColorButton
                            style={{marginLeft: "8px"}}
                            disabled={Boolean((clientUser.username && username === clientUser.username) && !b64Avatar)}
                            //@ts-ignore
                            onClick={async (event,
                                            setLoading) => {

                                setLoading(true);
                                let base64avatar = null;

                                if (b64Avatar) {
                                    base64avatar = b64Avatar.split(',')[1]
                                }

                                await shadowClient.patchUser({username: clientUser.username, base64avatar})
                                        .then(() => {
                                            setSaveError("");
                                            setUsername(clientUser.username);
                                            setNotificationMessage("Saved your profile");
                                        }).catch(e => {
                                            setSaveError(e.message)
                                        }).finally(() => {
                                            setLoading(false);
                                        })
                            }}
                    >
                        Save
                    </ColorButton>

                </DialogActions>

                <DialogContent>

                    <DialogContentText>
                        Change the login token to use a different account.
                    </DialogContentText>


                    <Box row alignItems={"center"}>
                        <TextField
                                sx={{marginRight: "10px"}}
                                margin="dense"
                                id="login_token"
                                label="Login token"
                                type={showPassword ? 'text' : 'password'}
                                fullWidth
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                onMouseDown={event => event.preventDefault()}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                }}
                                variant="standard"
                                value={loginToken || ""}
                                onChange={(event) => {
                                    setLoginToken(event.target.value);
                                }}
                        />

                        <ControlButton
                                label={"Copy login token"}
                                icon={ContentCopyOutlined}
                                onClick={() => {
                                    utils.copyToClipboard(clientUser.login_token).then(() => {
                                        setNotificationMessage("Copied your login token");
                                    }).catch(e => setSaveError(e.message));
                                }}
                        />

                        <PortalledSnackbar
                                message={notificationMessage}
                                onClose={() => setNotificationMessage("")}
                        />

                    </Box>
                </DialogContent>


                <DialogActions>
                    <Button
                            disabled={clientUser.login_token === loginToken}
                            onClick={() => {
                                setLoginToken(clientUser.login_token)
                            }}
                    >
                        Cancel
                    </Button>

                    <ColorButton
                            disabled={Boolean(loginToken && loginToken === clientUser.login_token)}
                            //@ts-ignore
                            onClick={async (_, setLoading) => {
                                try {
                                    if (!loginToken) {
                                        setSaveError("Please set a login token");
                                        return;
                                    }

                                    setLoading(true);

                                    const roomId = joinedRoomId;

                                    if (shadowClient.connected) {
                                        await shadowClient.disconnect();
                                    }

                                    logger.trace("logging in with login token %s", loginToken);
                                    await shadowClient.connect(loginToken);
                                    localStorage.setItem("shadowLoginToken", loginToken);
                                    logger.trace("set shadowLoginToken: %s", loginToken);
                                    clientUser = shadowClient.getClientUser();

                                    //@todo this might be an old user object
                                    if (roomId) {
                                        await shadowClient.joinRoomWithId(roomId);
                                    } else {
                                        const errorMessage = "Connected, but no anonymous rooms were available";
                                        setSaveError(errorMessage);
                                    }

                                    setSaveError("");
                                    setLoading(false);
                                    setNotificationMessage(`Loaded your account`);
                                } catch (e) {
                                    setLoading(false);
                                    setSaveError(e.message)
                                    throw e;
                                }
                            }}
                    >
                        Load account
                    </ColorButton>
                </DialogActions>
            </Box>
    );
};

export default observer(UserSettings);
